import Vec2 from 'vec2';

import { type BoundingBox, createBoundingBox } from './BoundingBox.utils';
import { SimpleLine } from './SimpleLine.utils';
import { convertFromRadiansToDegree } from './math-helper';

import { LineSegment } from '~/core/models';

/**
 * Math related helper functions
 *
 */
const MAX_PRECISION = 20;

export const multiplyVectorByScalar = (v: Vec2, s: number): Vec2 =>
  new Vec2(v.x * s, v.y * s);

export const addVectors = (v1: Vec2, v2: Vec2): Vec2 =>
  new Vec2(v1.x + v2.x, v1.y + v2.y);

export const deltaVectorFromTwoPoints = (
  v1: Vec2,
  v2: Vec2,
  delta: number
): Vec2 => {
  const diffVec = getVectorBetweenPoints(v1, v2);
  const magnitude = getDistanceBetween(v1, v2);

  return multiplyVectorByScalar(diffVec, delta / magnitude);
};

export const roundVector = (v: Vec2): Vec2 =>
  new Vec2(Math.round(v.x), Math.round(v.y));

export const subtractVectors = (v1: Vec2, v2: Vec2): Vec2 =>
  new Vec2(v1.x - v2.x, v1.y - v2.y);

export const roundVectorTo = (v: Vec2, digits = 0): Vec2 => {
  digits = Math.round(digits);
  if (Math.abs(digits) > MAX_PRECISION) {
    digits = Math.sign(digits) * MAX_PRECISION;
  }
  const scale = 10 ** digits;

  return new Vec2(
    Math.round(v.x * scale) / scale,
    Math.round(v.y * scale) / scale
  );
};
export const roundTo = (n: number, digits = 0): number => {
  digits = Math.round(digits);
  if (Math.abs(digits) > MAX_PRECISION) {
    digits = Math.sign(digits) * MAX_PRECISION;
  }
  const scale = 10 ** digits;

  return (n * scale) / scale;
};

export const getVectorBetweenPoints = (p1: Vec2, p2: Vec2): Vec2 =>
  new Vec2(p2.x - p1.x, p2.y - p1.y);

export const getDistanceBetween = (p1: Vec2, p2: Vec2): number =>
  Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));

export const getMidpoint = (p1: Vec2, p2: Vec2): Vec2 => {
  //2d:
  if (!p2) {
    return p1;
  }
  if (!p1) {
    return p2;
  }

  return new Vec2((p1.x + p2.x) / 2, (p1.y + p2.y) / 2);
};

export const isHorizontallyAligned = (
  p1: Vec2,
  p2: Vec2,
  round?: boolean
): boolean => {
  const y1 = p1.y;
  const y2 = p2.y;

  if (round) {
    return Math.round(y1) === Math.round(y2);
  }

  return y1 === y2;
};

export const isVerticallyAligned = (
  p1: Vec2,
  p2: Vec2,
  round?: boolean
): boolean => {
  const x1 = p1.x;
  const x2 = p2.x;

  if (round) {
    return Math.round(x1) === Math.round(x2);
  }

  return x1 === x2;
};

export const isHorizontallyOrVerticallyAligned = (
  p1: Vec2,
  p2: Vec2
): boolean => isHorizontallyAligned(p1, p2) || isVerticallyAligned(p1, p2);

export const getUnitNormal = (p1: Vec2, p2: Vec2): Vec2 => {
  if (isVerticallyAligned(p1, p2)) {
    return new Vec2(normalizeOneDimensional(p2.y - p1.y), 0);
  }
  if (isHorizontallyAligned(p1, p2)) {
    return new Vec2(0, normalizeOneDimensional(p2.x - p1.x));
  }
  const slope = -((p2.x - p1.x) / (p2.y - p1.y));

  return normalizeTwoDimensionalSlop(slope);
};

export const normalizeOneDimensional = (length: number): number => {
  if (length > 0) {
    return 1;
  }
  if (length < 0) {
    return -1;
  }

  return length;
};

export const normalizeTwoDimensionalSlop = (slop: number): Vec2 =>
  normalizeTwoDimensional(new Vec2(1, slop));

export const normalizeTwoDimensional = (vector: Vec2): Vec2 => {
  const length = getVectorLength(vector);

  if (length === 0) {
    return new Vec2(0, 0);
  }

  return new Vec2(vector.x / length, vector.y / length);
};

export const getVectorLength = (vector: Vec2): number =>
  Math.sqrt(Math.pow(vector.x, 2) + Math.pow(vector.y, 2));

export const getScalarLengthAlong = (
  deltaVector: Vec2,
  basisVector: Vec2
): number => {
  if (isZeroVector(deltaVector) || isZeroVector(basisVector)) {
    return 0;
  }
  deltaVector = projectVector(deltaVector, basisVector);
  const unsignedLength = getVectorLength(deltaVector);

  if (
    isOppositeSign(deltaVector.x, basisVector.x) ||
    isOppositeSign(deltaVector.y, basisVector.y)
  ) {
    return -1 * unsignedLength;
  }

  return unsignedLength;
};

export const isZeroVector = (v: Vec2): boolean => v.x === 0 && v.y === 0;

export const projectVector = (a: Vec2, b: Vec2): Vec2 =>
  new Vec2(a.x * b.x, a.y * b.y).divide(a.lengthSquared());

export const isOppositeSign = (a: number, b: number): boolean =>
  (a > 0 && b < 0) || (a < 0 && b > 0);

export const getScalarLengthAlongMatchingLinearVector = (
  deltaVector: Vec2,
  basisVector: Vec2
): number => {
  if (isZeroVector(deltaVector) || isZeroVector(basisVector)) {
    return 0;
  }
  const unsignedLength = getVectorLength(deltaVector);

  if (
    isOppositeSign(deltaVector.x, basisVector.x) ||
    isOppositeSign(deltaVector.y, basisVector.y)
  ) {
    return -1 * unsignedLength;
  }

  return unsignedLength;
};

export const pointsAreEqual = (p1: Vec2, p2: Vec2): boolean =>
  p1.x === p2.x && p1.y === p2.y;

export const transposePoint = (p: Vec2): Vec2 => new Vec2(p.y, p.x);

export const reverseVector = (v: Vec2): Vec2 => {
  if (!v) {
    return new Vec2(0, 0);
  }

  return new Vec2(-v.x, -v.y);
};

export const getScalarSlope = (p1: Vec2, p2?: Vec2): number => {
  if (p2) {
    return getScalarSlope(getVectorBetweenPoints(p1, p2));
  }
  if (p1.x === 0) {
    return NaN;
  }

  return p1.y / p1.x;
};

export const linesIntersect = (
  line1: SimpleLine,
  line2: SimpleLine
): boolean => {
  const intPoint = getInterceptPoint(
    getMidpoint(line1.point1, line1.point2),
    getVectorBetweenPoints(line1.point1, line1.point2),
    line2.point2,
    getVectorBetweenPoints(line2.point1, line2.point2)
  );

  if (intPoint != null && isFinite(intPoint.x) && isFinite(intPoint.y)) {
    return (
      lineContainsLinearPoint(line1, intPoint) &&
      lineContainsLinearPoint(line2, intPoint)
    );
  }

  return false;
};

export const linesShareAnEndPoint = (
  line1: SimpleLine,
  line2: SimpleLine
): boolean =>
  (Math.abs(line1.point1.x - line2.point1.x) < 0.1 &&
    Math.abs(line1.point1.y - line2.point1.y) < 0.1) ||
  (Math.abs(line1.point1.x - line2.point2.x) < 0.1 &&
    Math.abs(line1.point1.y - line2.point2.y) < 0.1) ||
  (Math.abs(line1.point2.x - line2.point1.x) < 0.1 &&
    Math.abs(line1.point2.y - line2.point1.y) < 0.1) ||
  (Math.abs(line1.point2.x - line2.point2.x) < 0.1 &&
    Math.abs(line1.point2.y - line2.point2.y) < 0.1);

export const linesIntersectBeyondEndPoints = (
  line1: SimpleLine,
  line2: SimpleLine
): boolean => {
  const midPoint = getMidpoint(line1.point1, line1.point2);
  const vector1 = getVectorBetweenPoints(line1.point1, line1.point2);
  const vector2 = getVectorBetweenPoints(line2.point1, line2.point2);
  const interceptPoint = getInterceptPoint(
    midPoint,
    vector1,
    line2.point2,
    vector2
  );

  if (
    interceptPoint &&
    isFinite(interceptPoint.x) &&
    isFinite(interceptPoint.y) &&
    !linesShareAnEndPoint(line1, line2)
  ) {
    return (
      lineContainsLinearPoint(line1, interceptPoint) &&
      lineContainsLinearPoint(line2, interceptPoint)
    );
  }

  return false;
};

export const lineContainsPoint = (
  line: SimpleLine,
  point: Vec2,
  dontRound: boolean,
  projectLine: boolean
): boolean => {
  let slopeVector = getVectorBetweenPoints(line.point1, line.point2);

  if (!dontRound) {
    slopeVector = roundVector(slopeVector);
  }
  if (slopeVector.x === 0) {
    if (projectLine) {
      const ySorted = [point.y, line.point1.y, line.point2.y].sort(
        (a, b) => a - b
      );

      return (
        point.x === line.point1.x &&
        numberIsBetween(ySorted[1], ySorted[0], ySorted[2])
      );
    }

    return (
      point.x === line.point1.x &&
      numberIsBetween(point.y, line.point1.y, line.point2.y)
    );
  }
  const slope = getScalarSlope(line.point1, line.point2);
  const yInt = solveForYIntercept(slope, line.point1);
  let yAtX = slope * point.x + yInt; //y = mx+b
  let pointY = point.y;

  if (!dontRound) {
    pointY = Math.round(pointY);
    yAtX = Math.round(yAtX);
  }

  if (!(pointY === yAtX)) {
    return false;
  }
  if (projectLine) {
    return true;
  }

  return lineContainsLinearPoint(line, point, dontRound);
};

export const lineContainsLinearPoint = (
  line: SimpleLine,
  point: Vec2,
  dontRound?: boolean
): boolean => {
  const vectorShift = reverseVector(line.point1);
  let lineVector = addVectors(line.point2, vectorShift);
  let newLinearPoint = addVectors(point, vectorShift);

  if (!dontRound) {
    lineVector = roundVector(lineVector);
    newLinearPoint = roundVector(newLinearPoint);
  }
  let lineEndsAt = getVectorLength(lineVector); //0 to that.
  let pointIsAt = getScalarLengthAlongMatchingLinearVector(
    newLinearPoint,
    lineVector
  );

  if (!dontRound) {
    lineEndsAt = Math.round(lineEndsAt);
    pointIsAt = Math.round(pointIsAt);
  }

  return numberIsBetween(pointIsAt, 0, lineEndsAt);
};

export const numberIsBetween = (num: number, a: number, b: number): boolean => {
  if (a > b) {
    return numberIsBetween(num, b, a);
  }

  //a <= b:
  return num >= a && num <= b;
};

export const intersectionOfSimpleLines = (
  line1: SimpleLine,
  line2: SimpleLine
): boolean | Vec2 =>
  intersectionOfLines(
    line1.point1.x,
    line1.point1.y,
    line1.point2.x,
    line1.point2.y,
    line2.point1.x,
    line2.point1.y,
    line2.point2.x,
    line2.point2.y
  );

export const intersectionOfVec2Lines = (
  start1: Vec2,
  end1: Vec2,
  start2: Vec2,
  end2: Vec2
): boolean | Vec2 =>
  intersectionOfLines(
    start1.x,
    start1.y,
    end1.x,
    end1.y,
    start2.x,
    start2.y,
    end2.x,
    end2.y
  );

/**
 * Determine the intersection point of two line segments
 * (x1,y1) and (x2,y2) make line1
 * (x3,y3) and (x4,y4) make line2
 * Return FALSE if the lines don't intersect.
 * line intercept math by Paul Bourke http://paulbourke.net/geometry/pointlineplane/
 */
export const intersectionOfLines = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  x3: number,
  y3: number,
  x4: number,
  y4: number
): boolean | Vec2 => {
  // Check if none of the lines are of length 0
  if ((x1 === x2 && y1 === y2) || (x3 === x4 && y3 === y4)) {
    return false;
  }

  const denominator = (y4 - y3) * (x2 - x1) - (x4 - x3) * (y2 - y1);

  // Lines are parallel
  if (denominator === 0) {
    return false;
  }

  const ua = ((x4 - x3) * (y1 - y3) - (y4 - y3) * (x1 - x3)) / denominator;
  const ub = ((x2 - x1) * (y1 - y3) - (y2 - y1) * (x1 - x3)) / denominator;

  // is the intersection along the segments
  if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
    return false;
  }

  // Return a object with the x and y coordinates of the intersection
  const x = x1 + ua * (x2 - x1);
  const y = y1 + ua * (y2 - y1);

  return new Vec2(x, y);
};

// ============================================================================
// INTERCEPT POINT HELPERS
// ============================================================================

const ZERO_VECTOR = new Vec2(0, 0);

/**
 * Checks if lines are parallel (same slope or both vertical).
 */
const areLinesParallel = (
  slopeVector: Vec2,
  interceptLineSlopeVector: Vec2
): boolean =>
  pointsAreEqual(slopeVector, interceptLineSlopeVector) ||
  (slopeVector.x === 0 && interceptLineSlopeVector.x === 0);

/**
 * Checks if a vector is a zero vector.
 */
const isZeroSlopeVector = (vector: Vec2): boolean =>
  pointsAreEqual(ZERO_VECTOR, vector);

/**
 * Handles the case where intercept line has zero slope vector.
 * Returns the intercept point if the starting line contains the intercept point.
 */
const handleZeroInterceptSlope = (
  startingPoint: Vec2,
  slopeVector: Vec2,
  interceptLinePoint: Vec2
): Vec2 | null => {
  const line = {
    point1: startingPoint,
    point2: addVectors(startingPoint, slopeVector),
  };

  return lineContainsPoint(line, interceptLinePoint, true, true)
    ? interceptLinePoint
    : null;
};

/**
 * Handles the case where starting line has zero slope vector.
 * Returns starting point if it lies on the intercept line.
 */
const handleZeroStartingSlope = (
  startingPoint: Vec2,
  interceptLinePoint: Vec2,
  interceptLineSlopeVector: Vec2
): Vec2 | null => {
  const line = {
    point1: interceptLinePoint,
    point2: addVectors(interceptLinePoint, interceptLineSlopeVector),
  };

  return lineContainsPoint(line, startingPoint, true, true)
    ? startingPoint
    : null;
};

/**
 * Handles vertical intercept line case.
 */
const handleVerticalInterceptLine = (
  startingPoint: Vec2,
  slope: number,
  interceptLinePoint: Vec2
): Vec2 => {
  const yValue = solveForYIntercept(
    slope,
    new Vec2(startingPoint.x - interceptLinePoint.x, startingPoint.y)
  );

  return new Vec2(interceptLinePoint.x, yValue);
};

/**
 * Calculates intersection using standard slope-intercept form.
 */
const calculateStandardIntersection = (
  startingPoint: Vec2,
  slopeVector: Vec2,
  interceptLinePoint: Vec2,
  interceptLineSlopeVector: Vec2
): Vec2 | null => {
  const slope = getScalarSlope(slopeVector);
  const interceptLineSlope = getScalarSlope(interceptLineSlopeVector);

  if (slope === interceptLineSlope) {
    return null;
  }

  const startingPointYIntercept = solveForYIntercept(slope, startingPoint);
  const interceptLineYIntercept = solveForYIntercept(
    interceptLineSlope,
    interceptLinePoint
  );

  const xVal =
    (interceptLineYIntercept - startingPointYIntercept) /
    (slope - interceptLineSlope);
  const yVal = slope * xVal + startingPointYIntercept;

  return new Vec2(xVal, yVal);
};

/**
 * Core intercept calculation (assumes vectors are already rounded).
 * Handles vertical starting line by transposing coordinates.
 */
const handleVerticalLine = (
  startingPoint: Vec2,
  slopeVector: Vec2,
  interceptLinePoint: Vec2,
  interceptLineSlopeVector: Vec2
): Vec2 | null => {
  const transposedIntercept = getInterceptPointCore(
    transposePoint(startingPoint),
    transposePoint(slopeVector),
    transposePoint(interceptLinePoint),
    transposePoint(interceptLineSlopeVector)
  );

  return transposedIntercept ? transposePoint(transposedIntercept) : null;
};

/**
 * Core intercept calculation (assumes vectors are already rounded).
 * This is the non-recursive workhorse that handles all edge cases.
 */
const getInterceptPointCore = (
  startingPoint: Vec2,
  slopeVector: Vec2,
  interceptLinePoint: Vec2,
  interceptLineSlopeVector: Vec2
): Vec2 | null => {
  // Same points
  if (pointsAreEqual(startingPoint, interceptLinePoint)) {
    return startingPoint;
  }

  // Parallel lines
  if (areLinesParallel(slopeVector, interceptLineSlopeVector)) {
    return null;
  }

  // Zero intercept slope vector
  if (isZeroSlopeVector(interceptLineSlopeVector)) {
    return handleZeroInterceptSlope(
      startingPoint,
      slopeVector,
      interceptLinePoint
    );
  }

  // Zero starting slope vector
  if (isZeroSlopeVector(slopeVector)) {
    return handleZeroStartingSlope(
      startingPoint,
      interceptLinePoint,
      interceptLineSlopeVector
    );
  }

  // Vertical starting line
  if (slopeVector.x === 0) {
    return handleVerticalLine(
      startingPoint,
      slopeVector,
      interceptLinePoint,
      interceptLineSlopeVector
    );
  }

  const slope = getScalarSlope(slopeVector);

  // Vertical intercept line
  if (interceptLineSlopeVector.x === 0) {
    return handleVerticalInterceptLine(
      startingPoint,
      slope,
      interceptLinePoint
    );
  }

  // Standard intersection calculation
  return calculateStandardIntersection(
    startingPoint,
    slopeVector,
    interceptLinePoint,
    interceptLineSlopeVector
  );
};

/**
 * Finds the intersection point between two lines defined by points and slope vectors.
 * Projects intercept line to get an answer.
 *
 * @param startingPoint - Starting point of first line
 * @param slopeVector - Direction vector of first line
 * @param interceptLinePoint - Point on second line
 * @param interceptLineSlopeVector - Direction vector of second line
 * @param dontRoundVectors - If true, skip rounding/normalization
 */
export const getInterceptPoint = (
  startingPoint: Vec2,
  slopeVector: Vec2,
  interceptLinePoint: Vec2,
  interceptLineSlopeVector: Vec2,
  dontRoundVectors?: boolean
): Vec2 | null => {
  if (dontRoundVectors) {
    return getInterceptPointCore(
      startingPoint,
      slopeVector,
      interceptLinePoint,
      interceptLineSlopeVector
    );
  }

  return getInterceptPointCore(
    roundVector(startingPoint),
    roundVector(slopeVector.normalize(true)),
    roundVector(interceptLinePoint),
    roundVector(interceptLineSlopeVector.normalize(true))
  );
};

export const solveForYIntercept = (slope: number, point: Vec2): number => {
  let pointYIntercept: number;

  if (point.x === 0 || slope === 0) {
    pointYIntercept = point.y;
  } else {
    pointYIntercept = point.y - slope * point.x;
  }

  return pointYIntercept;
};

export const calculateRadiusFromArc = (
  arcWidth: number,
  arcDepth: number
): number => {
  const halfLength = arcWidth / 2;

  return (arcDepth * arcDepth + halfLength * halfLength) / (2 * arcDepth);
};

export const calculateArcDepth = (arcWidth: number, radius: number): number => {
  const halfLength = arcWidth / 2;
  const depthRadical = Math.sqrt(radius * radius - halfLength * halfLength);

  return radius - depthRadical;
};

export const calculateArcLength = (
  arcWidth: number,
  arcDepth: number
): number => {
  const halfLength = arcWidth / 2;
  const radius =
    (arcDepth * arcDepth + halfLength * halfLength) / (2 * arcDepth);
  let angleInRadians = 2 * (Math.PI / 2 - Math.acos(halfLength / radius));

  if (arcDepth > arcWidth / 2) {
    angleInRadians = Math.PI * 2 - angleInRadians;
  }

  return angleInRadians * radius;
};

export const calculateAreaOfTriangle = (
  p1: Vec2,
  p2: Vec2,
  p3: Vec2
): number => {
  const side1 = getDistanceBetween(p1, p2);
  const side2 = getDistanceBetween(p2, p3);
  const side3 = getDistanceBetween(p3, p1);

  const s = (side1 + side2 + side3) / 2;

  return Math.sqrt(s * ((s - side1) * (s - side2) * (s - side3)));
};

export const getLineLength = (line: SimpleLine): number =>
  getDistanceBetween(line.point1, line.point2);

export const getLineVector = (line: SimpleLine): Vec2 =>
  getVectorBetweenPoints(line.point1, line.point2);

export const getInterceptBetweenSimpleLines = (
  line1: SimpleLine,
  line2: SimpleLine,
  dontRound = false
): Vec2 | null =>
  getInterceptPoint(
    line1.point1,
    getLineVector(line1),
    line2.point1,
    getLineVector(line2),
    dontRound
  );

export const getLineNormal = (line: SimpleLine): Vec2 =>
  getUnitNormal(line.point1, line.point2);

export const getLineMidpoint = (line: SimpleLine): Vec2 =>
  getMidpoint(line.point1, line.point2);

export const isHorizontalLine = (line: SimpleLine, round: boolean): boolean =>
  isHorizontallyAligned(line.point1, line.point2, round);

export const isVerticalLine = (line: SimpleLine, round: boolean): boolean =>
  isVerticallyAligned(line.point1, line.point2, round);

export const calculateAngleFromLines = (
  lineIn: SimpleLine,
  lineOut: SimpleLine
): number => {
  const curve1Point = lineIn.point1;
  const angleVertexPoint = lineIn.point2;
  const curve2Point = lineOut.point2;

  const v1 = reverseVector(
    getVectorBetweenPoints(curve1Point, angleVertexPoint)
  ).normalize();
  const v2 = getVectorBetweenPoints(angleVertexPoint, curve2Point).normalize();

  return v1.angleTo(v2);
};

export const calculateAngleFromRadiusAndChord = (
  radius: number,
  chordLength: number
): number => 2 * Math.asin(chordLength / (2 * radius));

export const calculateOutVectorFromAngle = (
  vectorIn: Vec2,
  angle: number,
  counterClockwise: boolean
): Vec2 => {
  if (counterClockwise) {
    angle = -1 * angle;
  }

  return vectorIn.rotate(angle, 0, true);
};

export const convertToRadians = (angle: number): number =>
  (angle / 360) * 2 * Math.PI;

export const closestToZero = (a: number, b: number): number => {
  const absA = Math.abs(a);
  const absB = Math.abs(b);

  if (absB < absA) {
    return b;
  }

  return a;
};

export const getLineBetween = (p1: Vec2, p2: Vec2): SimpleLine =>
  new SimpleLine(p1, p2);

export const isSameSign = (n1: number, n2: number): boolean => {
  if (n1 > 0 && n2 > 0) {
    return true;
  }

  return !!(n1 < 0 && n2 < 0);
};

//Returns the interior angle between 2 vectors in degrees
// https://onlinemschool.com/math/library/vector/angl/
export const angleBetween = (v1: Vec2, v2: Vec2): number => {
  const dotProduct = v1.x * v2.x + v1.y * v2.y;
  const cosOfTheta = dotProduct / (v1.length() * v2.length());

  return convertFromRadiansToDegree(Math.acos(cosOfTheta));
};

export const findBoundingBox = (points: Array<Vec2>): BoundingBox => {
  const boundingBox = createBoundingBox();

  if (!points || points.length === 0) {
    return boundingBox;
  }
  points.forEach((point, index) => {
    boundingBox.left =
      index === 0 || point.x < boundingBox.left ? point.x : boundingBox.left;
    boundingBox.top =
      index === 0 || point.y < boundingBox.top ? point.y : boundingBox.top;
    boundingBox.right =
      index === 0 || point.x > boundingBox.right ? point.x : boundingBox.right;
    boundingBox.bottom =
      index === 0 || point.y > boundingBox.bottom
        ? point.y
        : boundingBox.bottom;
  });

  return boundingBox;
};

/**
 * get an array of lines connecting all the points from a polygon in sequence
 * @param points - the array of points making up the polygon
 * @returns array of SimpleLine segments of points[0]->points[1], points[1]->points[2]...
 */
export const getLinesFromPolygonPoints = (points: Vec2[]): SimpleLine[] => {
  if (!points || points.length === 0) {
    return [];
  }

  return points.map((value, index, array) => {
    if (index < array.length - 1) {
      return new SimpleLine(value, array[index + 1]);
    } else {
      return new SimpleLine(value, array[0]);
    }
  });
};

// ============================================================================
// POLYGON COLLISION HELPERS
// ============================================================================

/**
 * Checks if any line from sourceLines intersects with any line from targetLines.
 * Uses Array.some() for early termination without explicit breaks.
 */
const doLinesIntersect = (
  sourceLines: SimpleLine[],
  targetLines: SimpleLine[]
): boolean =>
  sourceLines.some((sourceLine) =>
    targetLines.some((targetLine) => linesIntersect(sourceLine, targetLine))
  );

/**
 * tests if the source polygon (array of Vec2 points defining a polygon) intersects with any shape in the polyArray (an array of arrays of Vec2 points)
 *
 * ignoreIndex lets you specify a single shape in the shapeList that will always return false - this is used when the source polygon is from an existing shape in the shapes list and you don't want to consider it a match on itself
 * polyArray = [ [p1, p2, p3], [p10, p20, p30, p40] ]
 * sourcePoly = [ p100, p101, p102, p103]
 * assuming that the source polygon line (p101, p102) intersects with the second polygon line (p30, p40) in the shapes array...
 * result = [ false, true ]
 * NOTE: if the sourcePoly is fully contained in any of the polyArray polygons, it will return true.
 */
export const polygonCollisions = (
  sourcePoly: Vec2[],
  polyArray: Vec2[][],
  ignoreIndex?: number
): boolean[] => {
  if (polyArray.length === 0) {
    return [];
  }

  if (sourcePoly && sourcePoly.length === 0) {
    return new Array(polyArray.length).fill(false);
  }

  const sourceLines = getLinesFromPolygonPoints(sourcePoly);

  return polyArray.map((targetPoly, index) => {
    if (ignoreIndex === index) {
      return false;
    }

    const targetLines = getLinesFromPolygonPoints(targetPoly);

    if (targetLines.length === 0) {
      return false;
    }

    return doLinesIntersect(sourceLines, targetLines);
  });
};

/**
 * tests if any of one polygon's sides intersect with any of its other sides
 */
export const polygonCollidesWithItself = (sourcePoly: Vec2[]): boolean => {
  if (sourcePoly.length === 0) {
    return false;
  }

  const sourceLines = getLinesFromPolygonPoints(sourcePoly);

  return sourceLines.some((sourceLine) =>
    sourceLines.some((otherLine) =>
      linesIntersectBeyondEndPoints(sourceLine, otherLine)
    )
  );
};

// Finds minumum distance from the testPoly to any of the otherPolys
export const minDistanceToOtherPolys = (
  testPoly: Vec2[],
  otherPolys: Vec2[][]
): number => {
  let dist = Infinity;

  otherPolys.forEach((otherPoly) => {
    const testDist = polyToPolyDistance(testPoly, otherPoly);

    dist = Math.min(dist, testDist);
  });

  return dist;
};

export const polyToPolyDistance = (poly1: Vec2[], poly2: Vec2[]): number => {
  const polyPointsToEdges = (
    p0: Vec2,
    i: number,
    poly: Vec2[]
  ): LineSegment => {
    if (i < poly.length - 1) {
      return { startPoint: p0, endPoint: poly[i + 1] };
    } else {
      return { startPoint: p0, endPoint: poly[0] };
    }
  };

  const p1Edges: LineSegment[] = poly1.map(polyPointsToEdges);
  const p2Edges: LineSegment[] = poly2.map(polyPointsToEdges);

  let dist = Infinity;

  p1Edges.forEach((edge1) => {
    p2Edges.forEach((edge2) => {
      dist = Math.min(segmentToSegmentDistance(edge1, edge2), dist);
    });
  });

  return dist;
};

// Find the minimum distance between two line segments
// adapted from https://stackoverflow.com/questions/2824478/shortest-distance-between-two-line-segments
export const segmentToSegmentDistance = (
  l1: LineSegment,
  l2: LineSegment
): number => {
  const min = Math.min;
  let distance = pointSegmentDistance(l1, l2.startPoint);

  distance = min(pointSegmentDistance(l1, l2.endPoint), distance);
  distance = min(pointSegmentDistance(l2, l1.startPoint), distance);
  distance = min(pointSegmentDistance(l2, l1.endPoint), distance);

  return distance;
};

export const pointSegmentDistance = (
  line: LineSegment,
  point: Vec2
): number => {
  const { startPoint, endPoint } = line;

  let dx = endPoint.x - startPoint.x;
  let dy = endPoint.y - startPoint.y;

  if (dx === 0 && dy === 0) {
    return Math.hypot(point.x - startPoint.x, point.y - startPoint.y);
  }

  //Calculate the t that minimizes the distance.
  const t =
    ((point.x - startPoint.x) * dx + (point.y - startPoint.y) * dy) /
    (dx * dx + dy * dy);

  //See if this represents one of the segment's
  //end points or a point in the middle.
  if (t < 0) {
    dx = point.x - startPoint.x;
    dy = point.y - startPoint.y;
  } else if (t > 1) {
    dx = point.x - endPoint.x;
    dy = point.y - endPoint.y;
  } else {
    const nearX = startPoint.x + t * dx;
    const nearY = startPoint.y + t * dy;

    dx = point.x - nearX;
    dy = point.y - nearY;
  }

  return Math.hypot(dx, dy);
};
